import { buildUrl, createRequest } from './common/httpUtils'
import { appendAuthToken } from './common/session'
import { retrieveResponseErrorMessage } from '../utils/errorUtils'
import type { QrTagEventType } from '../constants/Constants'

const handleResponse = async <T>(response: Response): Promise<T> => {
  const data = await response.json()

  if (!response.ok) {
    const message = data?.error || data?.error_message || response.statusText
    throw new Error(message)
  }

  return data
}

export type QrTagSort =
  | 'name'
  | 'description'
  | 'qr_code_number'
  | 'latest_event'
  | 'latest_event_occurred_at'

export type QrTagSortOrder = 'asc' | 'desc'

export type GetGroupQrTagsOptions = {
  keyword?: string
  sort?: QrTagSort
  order?: QrTagSortOrder
  latest_event_type?: string
}

const normalizeGetGroupQrTagsOptions = (
  options?: string | GetGroupQrTagsOptions,
): GetGroupQrTagsOptions => {
  if (typeof options === 'string') {
    return { keyword: options }
  }

  return options ?? {}
}

export const getGroupQrTags = async (
  groupId: GroupId,
  options?: string | GetGroupQrTagsOptions,
): Promise<IQrTag[]> => {
  const normalizedOptions = normalizeGetGroupQrTagsOptions(options)
  const searchParams = new URLSearchParams()

  if (normalizedOptions.keyword) searchParams.set('keyword', normalizedOptions.keyword)
  if (normalizedOptions.sort) searchParams.set('sort', normalizedOptions.sort)
  if (normalizedOptions.order) searchParams.set('order', normalizedOptions.order)
  if (normalizedOptions.latest_event_type) {
    searchParams.set('latest_event_type', normalizedOptions.latest_event_type)
  }

  const basePath = `/api/v1/groups/${groupId}/qr_tags`
  const pathWithQuery = searchParams.toString().length > 0 ? `${basePath}?${searchParams.toString()}` : basePath
  const url = buildUrl(appendAuthToken(pathWithQuery))
  const request = createRequest(url, 'GET')
  const response = await fetch(request)
  const data = await handleResponse<{ qr_tags: IQrTag[] }>(response)
  return data.qr_tags || []
}

export const getQrTagEvents = async (
  groupId: GroupId,
  qrTagId: number,
  options: {
    per?: number
    page?: number
    event_type?: QrTagEventType
  } = {},
): Promise<IQrTagEvent[]> => {
  const searchParams = new URLSearchParams()
  if (options.per) searchParams.set('per', String(options.per))
  if (options.page) searchParams.set('page', String(options.page))
  if (options.event_type) searchParams.set('event_type', options.event_type)
  if (!searchParams.has('per')) searchParams.set('per', '10')
  const basePath = `/api/v1/groups/${groupId}/qr_tags/${qrTagId}/events`
  const urlPath = searchParams.toString().length > 0 ? `${basePath}?${searchParams.toString()}` : basePath
  const url = buildUrl(appendAuthToken(urlPath))
  const request = createRequest(url, 'GET')
  const response = await fetch(request)
  const data = await handleResponse<{ qr_tag_events: IQrTagEvent[] }>(response)
  return data.qr_tag_events || []
}

export const getQrTagLatestEvent = async (
  groupId: GroupId,
  qrTagId: number,
): Promise<IQrTagEvent | null> => {
  const events = await getQrTagEvents(groupId, qrTagId, { per: 1, page: 1 })
  const [latestEvent] = events
  return latestEvent ?? null
}

export const scanQrTagDigitCode = async (
  groupId: GroupId,
  digitCode: string,
): Promise<{ qr_tag: IQrTag }> => {
  const url = buildUrl(appendAuthToken(`/api/v1/groups/${groupId}/qr_code_scanner/scan`))
  const request = createRequest(
    url,
    'POST',
    true,
    'application/json',
    { digit_code: digitCode },
  )

  const response = await fetch(request)
  const data = await response.json()

  if (!response.ok) {
    switch (response.status) {
      case 403:
        throw new Error('このQRタグは現在のチームでは利用できません。')
      case 404:
        throw new Error('QRタグが見つかりませんでした。')
      case 422:
        throw new Error(data?.error || 'QRコードが有効ではありません。')
      default:
        throw new Error(retrieveResponseErrorMessage(data))
    }
  }

  return data
}

type CreateQrTagEventPayload = {
  event_type: QrTagEventType
  memo?: string
  latitude?: number
  longitude?: number
}

export const createQrTagEvent = async (
  groupId: GroupId,
  qrTagId: number,
  payload: CreateQrTagEventPayload,
): Promise<IQrTagEvent> => {
  if (!payload.event_type) {
    throw new Error('イベント種別を指定してください。')
  }

  const url = buildUrl(appendAuthToken(`/api/v1/groups/${groupId}/qr_tags/${qrTagId}/events`))
  const request = createRequest(
    url,
    'POST',
    true,
    'application/json',
    {
      event: {
        event_type: payload.event_type,
        latitude: payload.latitude,
        longitude: payload.longitude,
        notes: payload.memo,
      },
    },
  )
  const response = await fetch(request)
  const data = await response.json()

  if (!response.ok) {
    switch (response.status) {
      case 403:
        throw new Error('イベントを記録する権限がありません。')
      case 404:
        throw new Error('対象のQRタグが見つかりませんでした。')
      case 422:
        throw new Error(data?.error || '入力内容を確認してください。')
      default:
        throw new Error(retrieveResponseErrorMessage(data))
    }
  }

  return data.qr_tag_event
}

export const exportQrTagEventsCsv = async (
  groupId: GroupId,
  qrTagId: number,
): Promise<Blob> => {
  const url = buildUrl(`/api/v1/groups/${groupId}/qr_tags/${qrTagId}/events/export`)
  const request = createRequest(url, 'GET')
  const response = await fetch(request)

  if (!response.ok) {
    let message = response.statusText
    try {
      const data = await response.json()
      message = data?.error || data?.error_message || message
    } catch (error) {
      // ignore JSON parse error and fall back to statusText
    }
    throw new Error(message)
  }

  return response.blob()
}

export const buildQrTagEventsExportUrl = (groupId: GroupId, qrTagId: number): string => (
  buildUrl(appendAuthToken(`/api/v1/groups/${groupId}/qr_tags/${qrTagId}/events/export`))
)

export const updateQrTag = async (
  groupId: GroupId,
  qrTagId: number,
  params: {
    name?: string
    description?: string | null
  },
): Promise<IQrTag> => {
  const attributes: Record<string, string | null> = {}
  if (params.name !== undefined) attributes.name = params.name
  if (params.description !== undefined) attributes.description = params.description

  if (Object.keys(attributes).length === 0) {
    throw new Error('更新する項目を指定してください。')
  }

  const url = buildUrl(appendAuthToken(`/api/v1/groups/${groupId}/qr_tags/${qrTagId}`))
  const request = createRequest(
    url,
    'PATCH',
    true,
    'application/json',
    { qr_tag: attributes },
  )

  const response = await fetch(request)
  const data = await handleResponse<{ qr_tag: IQrTag }>(response)

  return data.qr_tag
}
